import {
  collection,
  deleteField,
  doc,
  FieldValue,
  getDocsFromServer,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { DateTime } from "luxon";
import _chunk from "lodash/chunk";
import type {
  CreateParams,
  CreateResult,
  FilterPayload,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  RaRecord,
  SortPayload,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { filterByOnboardingIssues } from "../utils/filterByOnboardingIssues";
import { filterByAusbildungsnachweisState } from "../utils/filterByAusbildungsnachweisState";
import { restrictAccessToDrivingSchoolIds } from "../backoffice.access_control";
import {
  applyFilter,
  applyPagination,
  applySort,
  classNameOf,
  fromFirestore,
  getFilterMode,
  reportError,
  sleep,
  zodDateTime,
} from "../backoffice.utils";
import { firestore } from "../firebase";
import { PostalAddress, PostalAddressSchema } from "../model/PostalAddress";
import { StudentStatus, StudentStatusEnum } from "../model/StudentStatus";
import { DateString, DateStringSchema } from "../model/DateString";
import { knownInvalidDocuments } from "./FirestoreProvider";
import { z } from "zod";
import { InvoiceAddress, InvoiceAddressSchema } from "../model/InvoiceAddress";
import { baseUrl as serverBaseUrl, getAuthenticatedServerClient, serverAPI } from "../api/server.api";
import { schemas, schemas as serverClientSchemas } from "../generated/serverClient";
import { BookedTraining, bookedTrainingsFromFirestore } from "../model/BookedTraining";
import { Onboarding } from "../model/Onboarding";
import { FeatureTogglesForStudents, FeatureTogglesForStudentsSchema } from "../model/FeatureToggles";
import { Money } from "../model/Money";
import { Cents } from "../model/Cents";
import { t } from "../model/types";
import { updateApplied } from "../utils/updateApplied";
import { instructorsProvider } from "./instructorsProvider";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { QuerySnapshot } from "@firebase/firestore";
import {
  BUS_ONLY_DRIVING_LICENSE_CLASSES,
  BUS_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
  CAR_ONLY_DRIVING_LICENSE_CLASSES,
  CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
  DrivingLicenseClass,
  MOTORCYCLE_DRIVING_LICENSE_CLASSES,
  TRACTOR_DRIVING_LICENSE_CLASSES,
  TRUCK_ONLY_DRIVING_LICENSE_CLASSES,
  TRUCK_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
} from "../model/DrivingLicenseClass";
import { safeLocalStorage } from "../utils/safeLocalStorage";
import _isEqual from "lodash/isEqual";
import intersection from "lodash/intersection";
import { capitalize } from "../utils/string";
import { withOnlyDefinedProperties } from "../utils/object";
import { Gender } from "../model/Gender";
import { DateWithoutTime, DateWithoutTimeSchema } from "../model/DateWithoutTime";
import { Salutation } from "../inputs/SalutationInput";
import { TheoryLessonCategory } from "../model/ProductType";

export const ApprovalStatusEnum = z.enum(["pending", "approved", "notApproved"]);
export type ApprovalStatus = z.infer<typeof ApprovalStatusEnum>;

export interface BookedQuote {
  quoteId: string;
  bundleName: string;
  startDate?: DateWithoutTime;
  endDate?: DateWithoutTime;
}

const AdministrativeOnboardingStateSchema = z.object({
  applicationApproved: ApprovalStatusEnum.nullish().transform((it) => it || "pending"),
  applicationFilledOut: z.boolean().nullish().default(false),
  applicationSent: z.boolean().nullish().default(false),
  applicationSubmitted: z.boolean().nullish().default(false),
  applicationSubmittedAt: DateStringSchema.nullish(),
  applicationNumber: z.optional(z.string()),
  eyeTestDone: z.boolean().nullish().default(false),
  firstAidCourseBooked: z.boolean().nullish().default(false),
  firstAidCourseDone: z.boolean().nullish().default(false),
  firstAidCourseVoucherRequested: z.boolean().nullish().default(false),
  firstAidCourseVoucherSent: z.boolean().nullish().default(false),
  photoTaken: z.boolean().nullish().default(false),
});

type AdministrativeOnboardingState = z.infer<typeof AdministrativeOnboardingStateSchema>;

const AdministrativeOnboardingForChangersStateSchema = z.object({
  applicationNumber: z.optional(z.string()),
  changeSubmittedAt: DateStringSchema.nullish(),
  changeApproved: ApprovalStatusEnum.nullish().transform((it) => it || "pending"),
  changeSubmitted: z.boolean().nullish().default(false),
  oldContractTerminated: z.boolean().nullish().default(false),
  trainingCertificateSent: z.boolean().nullish().default(false),
});

type AdministrativeOnboardingForChangersState = z.infer<typeof AdministrativeOnboardingForChangersStateSchema>;

const StudentStatusHistorySchema = z.array(
  z.object({
    changedAt: zodDateTime(),
    changedBy: z.string(),
    newValue: StudentStatusEnum,
    reason: z.optional(z.string()),
  }),
);

export type FinishedTheoryLessonsCountByCategory = Partial<Record<TheoryLessonCategory, number>>;

interface TheoryKPIs {
  theoryExams?: {
    [uid: string]: {
      date: DateTime;
      rsvp?: "pending" | "accepted" | "rejected" | "declined" | "canceled" | "no-show";
      result?: "passed" | "failed";
    };
  };
  theoryLearningProgress?: {
    numQuestions: number;
    numCorrectlyAnsweredQuestions: number;
    numIncorrectlyAnsweredQuestions: number;
  };
  hasPassedTheoryExam?: boolean;
  finishedTheoryLessonsCount?: number;
  finishedTheoryLessonsCountByCategory?: FinishedTheoryLessonsCountByCategory;
  mostRecentTheoryLessonDate?: Timestamp;
}

const StudentDunningStatusEnum = z.enum([
  "warningSent",
  "paymentReminderSent",
  "firstDunningNoticeSent",
  "secondDunningNoticeSent",
  "handedOverToPairFinance",
]);
const DunningCandidateProcessStatuses = StudentDunningStatusEnum.options;

const DunningProcessSchema = z
  .object({
    id: z.string().uuid(),
    status: StudentDunningStatusEnum,
    paused: z.boolean(),
    postponedUntil: t.dateTime().nullish(),
    outstandingAmount: z.number().nonnegative().int(),
    warningSentAt: t.dateTime().nullish(),
    paymentReminderSentAt: t.dateTime().nullish(),
    firstDunningNoticeSentAt: t.dateTime().nullish(),
    secondDunningNoticeSentAt: t.dateTime().nullish(),
    handedOverToPairFinanceAt: t.dateTime().nullish(),
  })
  .transform((val) => (val && val.outstandingAmount === 0 ? null : val));

export type DunningProcess = z.infer<typeof DunningProcessSchema>;

export type VerboseStudentStatus =
  | "Abgebrochen"
  | "Aktiv"
  | "Aktiv (Grundbetrag offen)"
  | "Aktiv (Kein Zahlungsmittel)"
  | "Aktiv (Onboarding fehlt)"
  | "Fertig"
  | "Inaktiv"
  | "Offene Zahlungen"
  | "Pausiert"
  | "Unbekannt";

export const studentStatusColors: Record<VerboseStudentStatus, string> = {
  Abgebrochen: "#888888",
  Fertig: "#888888",
  Inaktiv: "#888888",
  Unbekannt: "#888888",
  Aktiv: "#74A089",
  "Aktiv (Grundbetrag offen)": "#74A089",
  "Aktiv (Kein Zahlungsmittel)": "#74A089",
  "Aktiv (Onboarding fehlt)": "#74A089",
  Pausiert: "#B9CFC4",
  "Offene Zahlungen": "#E87B7B",
};

export function getStudentStatusDisplay(verboseStatus: VerboseStudentStatus | undefined): {
  label: string | undefined;
  color: string | undefined;
} {
  return {
    label: verboseStatus?.startsWith("Aktiv") ? "Aktiv" : verboseStatus,
    color: verboseStatus ? studentStatusColors[verboseStatus] : undefined,
  };
}

export const CreateStudentDtoSchema = schemas.CreateStudentDto.omit({
  legal: true, // no-sense
  package: true, // optionalField
  packagePrice: true, // optionalField
  nationality: true, // DE
  theoryExamLanguage: true, // de
  invoiceAddress: true, // Copy from contactData address, then admins can edit it in the student details view.
});

interface Guardian {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

type ProgressMilestone =
  | "fullyRegistered"
  | "theoryExamPassed"
  | "minNormalDrivingLessonsDone"
  | "schaltkompetenzDone"
  | "specialDrivingLessonsDone";

type StudentType = "newDriver" | "drivingSchoolChanger" | "licenseTransferrer" | "otherStudent";

interface OnboardingIssues {
  noLogin: boolean;
  noPaymentMethod?: boolean;
  noPrepaidCredits?: boolean;
}

type BillingProvider = z.infer<typeof serverClientSchemas.StudentDto>["billingProvider"];

export interface Student {
  id: string;
  data: StudentDocumentData;
  autovioUserId: string;
  firstName: string;
  lastName: string;
  callName: string;
  name: string;
  email?: string; // Notfications email
  avatarUrl: string;
  hubspotContactId?: string;
  applicationFeePercentage?: number;
  postalAddress: PostalAddress;
  status: StudentStatus | "";
  lastStatusChangeAt?: DateTime;
  administrativeOnboardingState?: AdministrativeOnboardingState;
  administrativeOnboardingForChangersState?: AdministrativeOnboardingForChangersState;
  onboardingIssues: OnboardingIssues;
  drivingLicenseApplicationStatus?: ApprovalStatus;
  verboseStatus: VerboseStudentStatus;
  verboseStatusSinceXxxDays: string;
  instructorIds: Array<string>;
  drivingSchoolId: string;
  branchId?: string;
  cityOfBirth?: string;
  dateOfBirth?: DateWithoutTime;
  startDate?: DateTime;
  birthName?: string;
  gender?: Gender;
  lastLogin?: {
    at: DateTime;
    platform: "Android" | "iOS";
  };
  kpis: {
    finishedTheoryLessonsCount: number;
    finishedDrivingLessonsCount: number;
    finishedNormalDrivingLessonsCount: number;
    finishedSimulatorDrivingLessonsCount: number;
    finishedSpecialDrivingLessonsCount: number;
    finishedManualGearLessonsCount: number;
    finishedOtherDrivingLessonsCount: number;
    mostRecentDrivingLessonDate?: DateTime;
    mostRecentTheoryLessonDate?: DateTime;
    plannedTheoryExamDate?: DateTime;
    theoryExams: {
      [calenderEventUid: string]: {
        date: DateTime;
        result?: "passed" | "failed";
        rsvp?: "pending" | "accepted" | "rejected" | "declined" | "canceled" | "no-show";
      };
    };
  };
  bookedTrainings: Array<BookedTraining>;
  bookedQuotes: Array<BookedQuote>;
  bookedTrainingsById: { [id: string]: BookedTraining };
  drivingLicenseClasses: Array<DrivingLicenseClass>;
  previousDrivingLicenseClasses: Array<DrivingLicenseClass>;
  bookedTrainingsSummary: string;
  activeOrMostRecentBookedTraining: BookedTraining;
  onboardings: Array<Onboarding>;
  progressMilestones?: ProgressMilestone[];
  stripeCustomerId?: string;
  paymentStrategy: "upfrontPayment" | "payAsYouDrive" | "purchaseOnAccount" | "externalBankTransfer";
  paymentMethod?: "card" | "sepa";
  hasPaidBaseFee: boolean;
  balance: Cents;
  balances: {
    open: Cents;
    paid?: Cents;
    allCredits: Cents;
    balance: Cents;
    prepaidCredits: Cents;
    migratedPrepaidCredits: Cents;
    revoked: Cents;
  };
  budget: Cents;
  theoryKPIs?: TheoryKPIs;
  maxBookableDrivingLessonsPerWeekLU?: number;
  maybeReadyForTheoryExam: boolean;
  isReadyForTheoryExam: boolean;
  needsTheoryExam: boolean;
  hasPassedTheoryExam: boolean;
  isSchoolChanger: boolean;
  bf17: boolean;
  nationality?: string;
  theoryExamLanguage?: string;
  needsGlasses?: boolean;
  archived: boolean;
  salutation?: Salutation;
  examinationAssignmentReceivedAt?: DateString | null;
  examinationAssignmentExpiresAt?: DateString | null;
  phoneNumber?: string;
  invoiceAddress?: InvoiceAddress;
  dunningProcess: DunningProcess | undefined | null;
  dunningPostponedUntil?: DateTime | null;
  dunningDisabled: boolean;
  featureToggles: FeatureTogglesForStudents;
  guaranteedDrivingLessonsPerWeek: number;
  guardian?: Guardian;
  subscriptionPackageId?: string;
  subscriptionPackageName?: string;
  subscriptionPackagePrice?: Money;
  subscriptionPackageDisabled?: boolean;
  subscriptionPackagePausedUntil?: DateTime | null;
  subscriptionRenewalDay?: number;
  studentType: StudentType;
  /** The ids of those branches where the student can book theory lessons. */
  theoryBranches: Array<string>;
  assignedLabels: Array<string>;
  minimumSimulatorUE?: number;
  historicalOutstandingPaymentsCount: number;
  vogelId?: string;
  practicalExamLocationUid?: string;
  billingProvider: BillingProvider;
}

export function isStudent(record: undefined | null | RaRecord): record is Student {
  return !!(record && Array.isArray(record.bookedTrainings));
}

interface StudentDocumentData {
  authUserUids: Array<string>; // <-- data model 2
  autovioUserId: string;
  email?: string;
  hubspotContactId?: string;
  publicProfile: {
    firstName?: string;
    lastName?: string;
  };
  postalAddress?: Partial<PostalAddress>;
  currentDrivingSchoolId: string; // <-- data model 2
  instructorUids: Array<string>; // <-- data model 2
  onboardings?: {
    [quoteId: string]: {
      baseFee: number;
      baseFeePaid: boolean;
      quoteId: string;
    };
  };
  training?: {
    status: Exclude<StudentStatus, "completed">;
    startDate: string;
    kpis?: {
      finishedTheoryLessonsCount: number;
      finishedDrivingLessonsCount: Record<string, number>;
      finishedManualGearLessonsCount?: number;
      mostRecentDrivingLessonDate?: Timestamp;
      mostRecentTheoryLessonDate?: Timestamp;
      plannedTheoryExamDate?: Timestamp;
      theoryExams: {
        [calenderEventUid: string]: {
          date: Timestamp;
          result?: "passed" | "failed";
        };
      };
      practicalExams: {
        [calenderEventUid: string]: {
          date: Timestamp;
          result?: "passed" | "failed";
        };
      };
    };
  };
  cityOfBirth?: string;
  dateOfBirth?: string;
  birthName?: string;
  gender?: Gender;
  stripeCustomerId?: string;
  paymentStrategy?: "upfrontPayment" | "payAsYouDrive" | "purchaseOnAccount" | "externalBankTransfer";
  paymentMethod?: "card" | "sepa";
  hasPaidBaseFee?: boolean;
  balance?: Cents;
  balances?: {
    open?: Cents;
    paid?: Cents;
    allCredits: Cents;
    balance: Cents;
    balance_v2: Cents;
    prepaidCredits: Cents;
    migratedPrepaidCredits?: Cents;
    revoked: Cents;
  };
  budget?: Cents;
  theoryKPIs?: TheoryKPIs;
  maxBookableDrivingLessonsPerWeekLU?: number;
  myBranches?: Record<string, string>;
  phoneNumber?: string;
  maybeReadyForTheoryExam?: boolean;
  isReadyForTheoryExam?: boolean;
  invoiceAddress?: InvoiceAddress;
  bookedQuotes?: { [quoteId: string]: any };
  bookedTrainings?: { [id: string]: any };
  previousDrivingLicenseClasses?: Array<DrivingLicenseClass>;
  administrativeOnboardingState?: any;
  administrativeOnboardingForChangersState?: any;
  applicationFeePercentage?: number;
  bf17?: boolean | FieldValue;
  nationality?: string;
  theoryExamLanguage?: string;
  needsGlasses?: boolean;
  archived?: boolean;
  salutation?: Salutation;
  dunningPostponedUntil?: Timestamp | FieldValue;
  dunningDisabled?: boolean | FieldValue;
  examinationAssignmentReceivedAt?: DateString | null;
  examinationAssignmentExpiresAt?: DateString | null;
  effectiveStatus: StudentStatus; // <-- data model 2
  verboseStatus: VerboseStudentStatus; // <-- data model 2
  dunningProcess?: any;
  featureToggles?: FeatureTogglesForStudents;
  studentStatusHistory?: any;
  guardian?: Guardian;
  lastLogin?: {
    at: Timestamp;
    platform: "Android" | "iOS";
  };
  subscriptionPackageId?: string;
  subscriptionPackage?: string;
  subscriptionPackagePrice?: Money;
  subscriptionPackageDisabled?: boolean;
  subscriptionPackagePausedUntil?: DateString | null;
  subscription?: {
    renewalDay?: number;
  };
  theoryBranches?: Array<string>;
  assignedLabels?: Array<string>;
  minimumSimulatorUE?: number;
  vogelId?: string;
  practicalExamLocationUid?: string;
  billingProvider?: BillingProvider;
}

class StudentsProvider extends AbstractDataProvider<Student> {
  private readonly _studentsByIdByDrivingSchoolId: Map<string, Map<string, Student>> = new Map();
  private readonly _studentsByIdPromisesByDrivingSchoolId: Map<string, Promise<Map<string, Student>>> = new Map();
  private readonly studentsByAutovioUserId: Map<string, Student> = new Map();

  private _webinarMode = safeLocalStorage.getItem("webinarMode", (s) => !!s);

  setWebinarMode(webinarMode: boolean): void {
    this._webinarMode = webinarMode;
    for (const map of this._studentsByIdByDrivingSchoolId.values()) {
      for (const student of map.values()) {
        this._convertStudent(student as any);
      }
    }
  }

  async getOne(_: string, { id }: GetOneParams): Promise<GetOneResult<Student>> {
    const student = this.getOneFromCache(id) ?? (await this._fetchOne(id));
    return { data: student };
  }

  getOneFromCache(id: string): Student | undefined {
    let student = this.studentsByAutovioUserId.get(id);
    if (student) {
      return student;
    }
    for (const studentsById of this._studentsByIdByDrivingSchoolId.values()) {
      student = studentsById.get(id);
      if (student) {
        return student;
      }
    }
  }

  async getList(_resource: string, { filter, sort, pagination }: GetListParams): Promise<GetListResult<Student>> {
    // Ignore "aktiv" filter which might be set on the "Vogel Fahrschulmanager" tab ...
    const { aktiv: _, ...effectiveFilter } = filter;
    const students = await this._getList(effectiveFilter);
    return applyPagination(applySort(students, this._convertSort(sort)), pagination);
  }

  async _getList(filter: FilterPayload): Promise<Array<Student>> {
    const {
      drivingSchoolId,
      progressMilestones,
      drivingSchoolIds,
      branchId,
      onboardingIssues,
      ausbildungsnachweisState,
      drivingLicenseClasses,
      ...filter_
    } = filter;
    const _getFilterMode = (key: string) => getFilterMode(filter, key);
    if (drivingSchoolId) {
      if (typeof drivingSchoolId !== "string") {
        throw new Error(`Unexpected filter value for drivingSchoolId: ${classNameOf(drivingSchoolId)}`);
      }
      let studentsMap = this._studentsByIdByDrivingSchoolId.get(drivingSchoolId)?.values();
      if (!studentsMap) {
        studentsMap = (await this._fetchStudentsOfDrivingSchool(drivingSchoolId)).values();
      }
      let students = Array.from(studentsMap);
      if (progressMilestones) {
        if (!Array.isArray(progressMilestones)) {
          throw new Error(`Unexpected filter value for progressMilestones: ${classNameOf(progressMilestones)}`);
        }
        const progressMode = _getFilterMode("progressMilestones");
        students = Array.from(students).filter((student) => {
          const matches =
            !!student.progressMilestones &&
            progressMilestones.every((milestone) => student.progressMilestones!.includes(milestone));
          return progressMode === "included" ? matches : !matches;
        });
      }
      if (branchId) {
        if (Array.isArray(branchId) && branchId.includes("__NO_BRANCH__")) {
          const otherBranchIds = branchId.filter((id) => id !== "__NO_BRANCH__");
          const branchMode = _getFilterMode("branchId");
          students = students.filter((student) => {
            const hasNoBranch = !student.branchId;
            let matches: boolean;
            if (otherBranchIds.length === 0) {
              matches = hasNoBranch;
            } else {
              const hasSelectedBranch = otherBranchIds.includes(student.branchId);
              matches = hasNoBranch || hasSelectedBranch;
            }
            return branchMode === "included" ? matches : !matches;
          });
        } else {
          filter_.branchId = branchId;
        }
      }
      if (onboardingIssues) {
        if (!Array.isArray(onboardingIssues)) {
          throw new Error(`Unexpected filter value for onboardingIssues: ${classNameOf(onboardingIssues)}`);
        }
        const onboardingMode = _getFilterMode("onboardingIssues");
        if (onboardingMode === "included") {
          students = filterByOnboardingIssues(students, onboardingIssues);
        } else {
          const matchingIds = new Set(filterByOnboardingIssues(students, onboardingIssues).map((s) => s.id));
          students = students.filter((s) => !matchingIds.has(s.id));
        }
      }
      if (Array.isArray(ausbildungsnachweisState)) {
        if (ausbildungsnachweisState.length !== 1 && ausbildungsnachweisState.length !== 2) {
          throw new Error(
            `Unexpected filter value for ausbildungsnachweisState: ${classNameOf(ausbildungsnachweisState)}`,
          );
        }
        const signatureRequests = await this._fetchAusbildungsnachweisSignatureRequests(students.map((s) => s.id));
        students = filterByAusbildungsnachweisState(
          students,
          signatureRequests,
          ausbildungsnachweisState,
          _getFilterMode("ausbildungsnachweisState"),
        );
      }
      if (Array.isArray(drivingLicenseClasses)) {
        if (drivingLicenseClasses.includes("BF17")) {
          const mode = _getFilterMode("drivingLicenseClasses");
          students = students.filter((student) => {
            const matches =
              intersection(student.drivingLicenseClasses, drivingLicenseClasses).length > 0 || student.bf17;
            return mode === "included" ? matches : !matches;
          });
        } else {
          filter_.drivingLicenseClasses = drivingLicenseClasses;
        }
      }
      return applyFilter(students, filter_);
    }
    if (filter.instructorIds) {
      const instructorIds: Array<string> = Array.isArray(filter.instructorIds)
        ? filter.instructorIds
        : typeof filter.instructorIds === "string"
          ? [filter.instructorIds]
          : (() => {
              throw new Error(`Unexpected filter value for instructorIds: ${classNameOf(filter.instructorIds)}`);
            })();
      const { data: instructors } = await instructorsProvider.getMany("instructors", { ids: instructorIds });
      if (!instructors.length) {
        return [];
      }
      const drivingSchoolId = instructors[0].drivingSchoolId;
      if (instructors.every((it) => it.drivingSchoolId === drivingSchoolId)) {
        return this._getList({ ...filter_, drivingSchoolId });
      }
    }
    if (Array.isArray(drivingSchoolIds)) {
      return this._fetchAll({ ...filter_, drivingSchoolId: drivingSchoolIds }, drivingSchoolIds);
    }
    return this._fetchAll(filter);
  }

  async getMany(_resource: string, { ids, meta }: GetManyParams): Promise<GetManyResult<Student>> {
    const drivingSchoolId =
      restrictAccessToDrivingSchoolIds?.length === 1
        ? restrictAccessToDrivingSchoolIds[0]
        : (meta ?? {}).drivingSchoolId;
    if (!drivingSchoolId) {
      let stack = new Error().stack ?? "\n";
      stack = stack.substring(stack.indexOf("\n"));
      console.info(
        `drivingSchoolId is missing in params.meta -- calling studentsProvider.getOne(...) ${ids.length} times ...`,
        stack,
      );
      const students = await Promise.all(ids.map((id) => this.getOne("students", { id }).then((r) => r.data)));
      return { data: students };
    }
    let studentsById = this._studentsByIdByDrivingSchoolId.get(drivingSchoolId);
    if (!studentsById) {
      studentsById = await this._fetchStudentsOfDrivingSchool(drivingSchoolId);
    }
    const students: Array<Student> = [];
    for (const id of ids) {
      const student = studentsById.get(id.toString());
      if (student) {
        students.push(student);
      }
    }
    return { data: students };
  }

  async getManyReference(
    resource: string,
    { target, id, pagination, sort, filter }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<Student>> {
    return this.getList(resource, { pagination, sort, filter: { ...filter, [target]: id } });
  }

  async create(resource: string, params: CreateParams): Promise<CreateResult<Student>> {
    const serverClient = await getAuthenticatedServerClient();

    const studentData = schemas.CreateStudentDto.omit({ legal: true }).parse({
      ...params.data,
      country: "DE",
      nationality: "DE",
      theoryExamLanguage: "de",
      contactData: {
        ...params.data.contactData,
        phone: `+49${params.data.contactData.phone}`,
      },
      loginEmail: params.data.contactData.email,
      assistedDrivingBF17: params.data.assistedDrivingBF17 ? JSON.parse(params.data.assistedDrivingBF17) : undefined,
      needsGlasses: params.data.needsGlasses ? JSON.parse(params.data.needsGlasses) : undefined,
      guardian: params.data.guardian // handle conditionally rendered guardian with value object of undefined values
        ? Object.values(params.data.guardian).some((it) => Boolean(it))
          ? params.data.guardian
          : undefined
        : undefined,
    });

    const q = query(
      collection(firestore, "students"),
      where("currentDrivingSchoolId", "==", studentData.drivingSchoolId),
    );

    let createdStudentId: string | undefined;
    const createdStudentPromise = new Promise<void>((resolve) => {
      const unsubscribe = onSnapshot(q, (querySnap) => {
        querySnap.docChanges().find((change) => {
          if (change.type === "added") {
            const student = this.fromFirestore(change.doc);
            if (student.drivingSchoolId === studentData.drivingSchoolId && student.email === studentData.loginEmail) {
              createdStudentId = change.doc.data().authUserUids[0];
              unsubscribe();
              resolve();
            }
          }
        });
      });
    });
    await serverClient.createStudent(studentData);
    await createdStudentPromise;
    return this.getOne(resource, { id: createdStudentId });
  }

  async update(resource: string, { id, previousData, data }: UpdateParams<Student>): Promise<UpdateResult<Student>> {
    const delta = Object.fromEntries(
      Object.entries(data).filter(([k, v]) => !_isEqual(v, (previousData as any)[k])),
    ) as Partial<Student>;
    if (Object.keys(delta).length === 0) {
      return { data: previousData };
    }
    if (delta.theoryBranches) {
      // Preparation for the updateReceived promise ...
      delta.theoryBranches.sort();
    }
    let cancelUpdateReceived = () => undefined;
    let startUpdateReceivedTimeout = () => undefined;
    const updateReceived = new Promise<void>((resolve, reject) => {
      let settled = false;
      const timeout = { id: undefined as ReturnType<typeof setTimeout> | undefined };
      const finish = (complete: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        unsubscribe();
        if (timeout.id) {
          clearTimeout(timeout.id);
        }
        complete();
      };
      const unsubscribe = this.onUpdate(previousData, (student) => {
        if (delta.theoryBranches) {
          student.theoryBranches.sort();
          // ... now the order of delta.theoryBranches and student.theoryBranches
          // is the same and updateApplied(...) will work as expected ...
        }
        if (updateApplied(delta, student)) {
          finish(resolve);
        }
      });
      startUpdateReceivedTimeout = () => {
        if (settled || timeout.id !== undefined) {
          return;
        }
        timeout.id = setTimeout(() => {
          finish(() => reject(new Error("Update timeout: changes did not appear in Firestore")));
        }, 10000);
      };

      cancelUpdateReceived = () => {
        finish(() => reject(new Error("Update listener cancelled")));
      };
    });
    /**
     * SERVER_PROPERTIES is a list of properties that need to be updated on the server using the according API.
     * They should not be updated in Firestore directly, the server will take care of that. Hence we need to wait
     * for the server to respond before we can return the updated student.
     */
    const SERVER_PROPERTIES = [
      "firstName",
      "lastName",
      "phoneNumber",
      "guardian",
      "email",
      "theoryBranches",
      "assignedLabels",
      "nationality",
      "theoryExamLanguage",
      "needsGlasses",
      "salutation",
      "previousDrivingLicenseClasses",
      "hubspotContactId",
      "birthName",
      "gender",
      "archived",
      "subscriptionPackageId",
      "dunningPostponedUntil",
    ];
    try {
      if (SERVER_PROPERTIES.some((key) => key in delta)) {
        const {
          firstName,
          lastName,
          phoneNumber,
          guardian,
          email,
          theoryBranches,
          assignedLabels,
          nationality,
          hubspotContactId,
          theoryExamLanguage,
          needsGlasses,
          salutation,
          previousDrivingLicenseClasses,
          gender,
          birthName,
          subscriptionPackageId,
          dunningPostponedUntil,
          archived,
        } = delta;
        const serverClient = await getAuthenticatedServerClient();
        const updateStudentData = schemas.UpdateStudentDto.parse({
          ...(firstName || lastName || phoneNumber || email || gender || birthName
            ? {
                user: {
                  ...(firstName ? { firstName } : {}),
                  ...(lastName ? { lastName } : {}),
                  ...(phoneNumber ? { phoneNumber } : {}),
                  ...(email ? { email } : {}),
                  ...(gender ? { gender } : {}),
                  ...(birthName ? { birthName } : {}),
                },
              }
            : {}),
          ...(guardian ? { guardian } : {}),
          ...(theoryBranches ? { theoryBranches } : {}),
          ...(assignedLabels ? { assignedLabels } : {}),
          ...(nationality ? { nationality } : {}),
          ...(theoryExamLanguage ? { theoryExamLanguage } : {}),
          ...(needsGlasses !== undefined && { needsGlasses }),
          ...(archived !== undefined && { archived }),
          ...(salutation !== undefined && { salutation }),
          ...(hubspotContactId ? { hubspotContactId } : {}),
          ...(previousDrivingLicenseClasses && { previousDrivingLicenseClasses }),
          ...(subscriptionPackageId && { subscriptionPackageId }),
          ...(dunningPostponedUntil !== undefined && { dunningPostponedUntil: dunningPostponedUntil?.toISO() ?? null }),
        });
        await serverClient.updateStudent(updateStudentData, { params: { id } });
      }
      if ("subscriptionRenewalDay" in delta && delta.subscriptionRenewalDay !== undefined) {
        const serverClient = await getAuthenticatedServerClient();
        await serverClient.updateStudentSubscription(
          schemas.UpdateStudentSubscriptionDto.parse({ renewalDay: delta.subscriptionRenewalDay }),
          { params: { studentId: id } },
        );
      }
      // This is dangerous: We rely here on the fact, that update is only called with data,
      // which adheres to the StudentDocumentData interface (except for the SERVER_PROPERTIES).
      const firestoreUpdateData = Object.fromEntries(
        Object.entries(this.toFirestore(delta, previousData)).filter(
          ([key, value]) => typeof value !== "undefined" && !SERVER_PROPERTIES.includes(key),
        ),
      );
      if (firestoreUpdateData.subscriptionPackagePausedUntil === null) {
        firestoreUpdateData.subscriptionPackagePausedUntil = deleteField();
      }
      if (firestoreUpdateData.practicalExamLocationUid === null) {
        firestoreUpdateData.practicalExamLocationUid = deleteField();
      }
      // Truncate time from dateOfBirth (dateOfBirth must be saved in the format "dd-MM-yyyy") ...
      const dateOfBirth = firestoreUpdateData.dateOfBirth?.toString();
      if (dateOfBirth && dateOfBirth.length > 10 && dateOfBirth[10] === "T") {
        firestoreUpdateData.dateOfBirth = dateOfBirth.substring(0, 10);
      }
      if (Object.entries(firestoreUpdateData).length > 0) {
        console.info(`Updating users/${id} ...`, firestoreUpdateData);
        await setDoc(doc(firestore, `users/${id}`), firestoreUpdateData, { merge: true });
      }
      startUpdateReceivedTimeout();
      await updateReceived;
    } catch (error) {
      cancelUpdateReceived();
      await updateReceived.catch(() => undefined);
      throw error;
    }
    return this.getOne(resource, { id });
  }

  onUpdate(student: Student, listener: (updatedStudent: Student) => void): () => void {
    const unsubscribe = onSnapshot(doc(firestore, `students/${student.autovioUserId}`), (snapshot) => {
      const updatedStudent = this.fromFirestore(snapshot);
      const { drivingSchoolId } = updatedStudent;
      const cache = this._studentsByIdByDrivingSchoolId.get(drivingSchoolId);
      if (cache) {
        cache.set(student.id, updatedStudent);
      }
      this.studentsByAutovioUserId.set(student.autovioUserId, updatedStudent);
      listener(updatedStudent);
    });
    return unsubscribe;
  }

  async getDunningCandidateStudents(dunningDrivingSchoolIds: Set<string>): Promise<Student[]> {
    const studentCollection = collection(firestore, "students");
    // Keeping each query to one field avoids the composite indexes that a combined
    // dunning-status and driving-school query would need.
    const snapshots = await Promise.all([
      getDocsFromServer(query(studentCollection, where("effectiveStatus", "==", "outstandingPayments"))),
      getDocsFromServer(query(studentCollection, where("balance", "<", 0))),
      getDocsFromServer(query(studentCollection, where("balances.balance", "<", 0))),
      getDocsFromServer(query(studentCollection, where("balances.balance_v2", "<", 0))),
      getDocsFromServer(
        query(studentCollection, where("dunningProcess.status", "in", DunningCandidateProcessStatuses)),
      ),
    ]);
    const candidateDocuments = new Map(
      snapshots.flatMap((snapshot) => snapshot.docs).map((document) => [document.id, document]),
    );
    const students: Student[] = [];
    for (const document of candidateDocuments.values()) {
      if (!dunningDrivingSchoolIds.has(document.data().currentDrivingSchoolId)) {
        continue;
      }
      try {
        const student = this.fromFirestore(document);
        students.push(student);
        this.studentsByAutovioUserId.set(student.autovioUserId, student);
      } catch (error) {
        const { path } = document.ref;
        if (!knownInvalidDocuments.has(path)) {
          knownInvalidDocuments.add(path);
          console.info(`Failed to parse Firestore document ${path} -- ignoring it`, error);
        }
      }
    }
    return students;
  }

  fromFirestore(snapshot: QueryDocumentSnapshot<any, any>): Student {
    const data = snapshot.data() as StudentDocumentData;
    const firstName = data.publicProfile.firstName ?? "???";
    const callName = _callName(data);
    const lastName = data.publicProfile.lastName ?? "???";
    const bookedQuotes = bookedQuotesFromFirestore(data.bookedQuotes ?? {});
    const bookedTrainings = bookedTrainingsFromFirestore(data.bookedTrainings ?? {});
    const bookedTrainingsById = Object.fromEntries(bookedTrainings.map((it) => [it.id, it]));
    const onboardings = Object.values(data.onboardings ?? {}) as Array<Onboarding>;
    const drivingSchoolId = data.currentDrivingSchoolId;
    if (!drivingSchoolId) {
      throw new Error(`${snapshot.ref.path} has no driving school`);
    }
    const drivingSchool = drivingSchoolsProvider.getOneFromCache(drivingSchoolId);
    if (!drivingSchool) {
      throw new Error(`${snapshot.ref.path} has invalid driving school`);
    }
    const status = data.effectiveStatus;
    let lastStatusChangeAt: undefined | DateTime;
    if (status === "completed") {
      for (const training of bookedTrainings) {
        for (const practicalExam of Object.values(training.kpis?.practicalExams ?? {})) {
          if (practicalExam.result === "passed") {
            if (lastStatusChangeAt) {
              DateTime.max(lastStatusChangeAt, practicalExam.date);
            } else {
              lastStatusChangeAt = practicalExam.date;
            }
          }
        }
      }
    }
    if (!lastStatusChangeAt && data.studentStatusHistory) {
      try {
        const studentStatusHistory = StudentStatusHistorySchema.parse(data.studentStatusHistory);
        const lastEntry = studentStatusHistory.at(-1);
        if (lastEntry?.newValue === status) {
          lastStatusChangeAt = lastEntry.changedAt;
        }
      } catch {
        // Ignored
      }
    }
    const theoryKPIs = data.theoryKPIs ?? {};
    if (!theoryKPIs.hasPassedTheoryExam) {
      if (
        bookedTrainings.some((it) => it.hasPassedTheoryExam) ||
        (theoryKPIs.theoryExams && Object.values(theoryKPIs.theoryExams ?? {}).some((it) => it.result === "passed"))
      ) {
        theoryKPIs.hasPassedTheoryExam = true;
      }
    }

    const administrativeOnboardingState = data.administrativeOnboardingState
      ? AdministrativeOnboardingStateSchema.parse(data.administrativeOnboardingState)
      : undefined;
    const administrativeOnboardingForChangersState = data.administrativeOnboardingForChangersState
      ? AdministrativeOnboardingForChangersStateSchema.parse(data.administrativeOnboardingForChangersState)
      : undefined;

    const guaranteedDrivingLessonsPerWeek = bookedTrainings.reduce((prev, it) => {
      if (!it.isFinished && it.guaranteedDrivingLessonsPerWeek) {
        return Math.max(prev, it.guaranteedDrivingLessonsPerWeek);
      }
      return prev;
    }, 0);

    let bookedTrainingsSummary = bookedTrainings.map((it) => it.shortLabel).join(",\u00A0");
    if (data.bf17) {
      bookedTrainingsSummary = `${bookedTrainingsSummary} (BF17)`;
    }
    const lastLogin = data.lastLogin ? { ...data.lastLogin, at: fromFirestore(data.lastLogin.at) } : undefined;
    const id = data.authUserUids ? data.authUserUids[0] : snapshot.id;
    const branchId = (data.myBranches ?? {})[drivingSchoolId];
    const historicalOutstandingPaymentsCount = data.studentStatusHistory
      ? StudentStatusHistorySchema.parse(data.studentStatusHistory).reduce((acc, entry) => {
          if (entry.newValue === "outstandingPayments") {
            acc++;
          }
          return acc;
        }, 0)
      : 0;
    let studentType: StudentType = "otherStudent";
    if (bookedTrainings.some((it) => it.drivingSchoolChange)) {
      studentType = "drivingSchoolChanger";
    } else if (bookedTrainings.some((it) => it.bundleName.toLowerCase().includes("umschreib"))) {
      studentType = "licenseTransferrer";
    } else if (bookedTrainings.some((it) => it.isForDrivingLicense)) {
      studentType = "newDriver";
    }
    const onboardingIssues = {
      noLogin: !data.lastLogin,
      noPaymentMethod: data.paymentStrategy === "payAsYouDrive" && !data.paymentMethod,
      noPrepaidCredits: data.paymentStrategy === "upfrontPayment" && !data.balances?.prepaidCredits,
    };

    const student = this._convertStudent({
      id,
      data,
      autovioUserId: data.autovioUserId,
      administrativeOnboardingState,
      administrativeOnboardingForChangersState,
      onboardingIssues,
      applicationFeePercentage: data.applicationFeePercentage,
      hubspotContactId: data.hubspotContactId,
      _firstName: firstName,
      _callName: callName,
      _lastName: lastName,
      _name: `${firstName} ${lastName}`,
      email: data.email,
      avatarUrl: `${serverBaseUrl}/user/${id}/avatar`,
      drivingSchoolId,
      branchId,
      instructorIds: data.instructorUids,
      status,
      lastStatusChangeAt,
      verboseStatus: "Unbekannt",
      verboseStatusSinceXxxDays: "...",
      bookedTrainingsSummary,
      _postalAddress: PostalAddressSchema.parse(data.postalAddress ?? {}),
      _invoiceAddress: data.invoiceAddress ? InvoiceAddressSchema.parse(data.invoiceAddress) : undefined,
      cityOfBirth: data.cityOfBirth,
      dateOfBirth: data.dateOfBirth ? DateWithoutTime.fromString(data.dateOfBirth) : undefined,
      startDate: data.training?.startDate ? DateTime.fromISO(data.training?.startDate) : undefined,
      lastLogin,
      kpis: {
        finishedTheoryLessonsCount: data.training?.kpis?.finishedTheoryLessonsCount ?? 0,
        finishedDrivingLessonsCount: this.sumDrivingLessonsCount(
          [
            "normal",
            "simulator",
            "ueberlandfahrt",
            "autobahnfahrt",
            "nachtfahrt",
            "praktischePruefung",
            "schaltkompetenz",
          ],
          data.training?.kpis?.finishedDrivingLessonsCount,
        ),
        finishedSimulatorDrivingLessonsCount: this.sumDrivingLessonsCount(
          ["simulator"],
          data.training?.kpis?.finishedDrivingLessonsCount,
        ),
        finishedNormalDrivingLessonsCount: this.sumDrivingLessonsCount(
          ["normal"],
          data.training?.kpis?.finishedDrivingLessonsCount,
        ),
        finishedSpecialDrivingLessonsCount: this.sumDrivingLessonsCount(
          ["ueberlandfahrt", "autobahnfahrt", "nachtfahrt"],
          data.training?.kpis?.finishedDrivingLessonsCount,
        ),
        finishedManualGearLessonsCount: data.training?.kpis?.finishedManualGearLessonsCount ?? 0,
        finishedOtherDrivingLessonsCount: this.sumDrivingLessonsCount(
          ["praktischePruefung", "schaltkompetenz"],
          data.training?.kpis?.finishedDrivingLessonsCount,
        ),
        mostRecentDrivingLessonDate: fromFirestore(data.training?.kpis?.mostRecentDrivingLessonDate),
        mostRecentTheoryLessonDate: fromFirestore(data.training?.kpis?.mostRecentTheoryLessonDate),
        plannedTheoryExamDate: fromFirestore(data.training?.kpis?.plannedTheoryExamDate),
        theoryExams: fromFirestore(data.training?.kpis?.theoryExams ?? {}) as any,
      },
      bookedQuotes,
      bookedTrainings,
      bookedTrainingsById,
      drivingLicenseClasses: bookedTrainings.map((it) => it.drivingLicenseClass),
      previousDrivingLicenseClasses: data.previousDrivingLicenseClasses ?? [],
      activeOrMostRecentBookedTraining: bookedTrainings[0], // ... will be overwritten below
      onboardings,
      stripeCustomerId: data.stripeCustomerId,
      paymentStrategy: data.paymentStrategy ?? "payAsYouDrive",
      paymentMethod: data.paymentMethod,
      hasPaidBaseFee: data.hasPaidBaseFee ?? false,
      balance: data.balances?.balance_v2 ?? data.balances?.balance ?? data.balance ?? 0,
      balances: {
        open: data.balances?.open ?? 0,
        paid: data.balances?.paid,
        allCredits: data.balances?.allCredits ?? 0,
        balance: data.balances?.balance_v2 ?? data.balances?.balance ?? data.balance ?? 0,
        prepaidCredits: data.balances?.prepaidCredits ?? 0,
        migratedPrepaidCredits: data.balances?.migratedPrepaidCredits ?? 0,
        revoked: data.balances?.revoked ?? 0,
      },
      budget: data.budget ?? 0,
      theoryKPIs: fromFirestore(theoryKPIs),
      maxBookableDrivingLessonsPerWeekLU: data.maxBookableDrivingLessonsPerWeekLU,
      maybeReadyForTheoryExam: !!data.maybeReadyForTheoryExam,
      isReadyForTheoryExam: !!data.isReadyForTheoryExam,
      needsTheoryExam: false,
      hasPassedTheoryExam: Object.values(data.theoryKPIs?.theoryExams ?? {}).some(({ result }) => result === "passed"),
      isSchoolChanger: bookedTrainings.some((it) => it.drivingSchoolChange),
      bf17: !!data.bf17,
      nationality: data.nationality,
      theoryExamLanguage: data.theoryExamLanguage,
      needsGlasses: data.needsGlasses,
      archived: !!data.archived,
      salutation: data.salutation,
      dunningPostponedUntil: t.dateTime().optional().parse(data.dunningPostponedUntil),
      dunningDisabled: !!data.dunningDisabled,
      examinationAssignmentReceivedAt: DateStringSchema.nullish().parse(data.examinationAssignmentReceivedAt),
      examinationAssignmentExpiresAt: DateStringSchema.nullish().parse(data.examinationAssignmentExpiresAt),
      _phoneNumber: data.phoneNumber,
      dunningProcess:
        // If the student is in the status "active" the dunning process has been canceled ...
        status === "active" ? null : DunningProcessSchema.nullish().parse(data.dunningProcess),
      featureToggles: _getEffectiveFeatureToggles(data.featureToggles, bookedTrainings, data.billingProvider),
      guaranteedDrivingLessonsPerWeek,
      guardian: data.guardian,
      subscriptionPackageId: data.subscriptionPackageId,
      subscriptionPackageName: data.subscriptionPackage,
      subscriptionPackagePrice: data.subscriptionPackagePrice,
      subscriptionPackageDisabled: !!data.subscriptionPackageDisabled,
      subscriptionPackagePausedUntil: data.subscriptionPackagePausedUntil
        ? DateTime.fromISO(data.subscriptionPackagePausedUntil)
        : null,
      subscriptionRenewalDay: data.subscription?.renewalDay,
      studentType,
      theoryBranches: data.theoryBranches ?? (branchId ? [branchId] : drivingSchool.branches.map((it) => it.uid)),
      assignedLabels: data.assignedLabels ?? [],
      minimumSimulatorUE: data.minimumSimulatorUE,
      historicalOutstandingPaymentsCount,
      billingProvider: data.billingProvider ?? "autovio",
      gender: data.gender ?? undefined,
      birthName: data.birthName ?? "",
      vogelId: data.vogelId,
      practicalExamLocationUid: data.practicalExamLocationUid,
    });
    student.needsTheoryExam =
      !student.hasPassedTheoryExam && bookedTrainings.some((it) => !it.isFinished && it.includesTheoryExam);
    student.activeOrMostRecentBookedTraining = _inferActiveOrMostRecentBookedTraining(student);
    student.verboseStatus = data.verboseStatus;
    student.progressMilestones = _inferProgressMilestones(
      student,
      data.training?.kpis?.finishedDrivingLessonsCount.schaltkompetenz,
    );
    student.verboseStatusSinceXxxDays = _renderVerboseStatusSinceXxxDays(student);
    if (student.isSchoolChanger) {
      if (student.administrativeOnboardingState) {
        delete student.administrativeOnboardingState; // <-- Hide the Tab "FÜHRERSCHEINANTRAG"
      }
      if (!student.administrativeOnboardingForChangersState) {
        student.administrativeOnboardingForChangersState = AdministrativeOnboardingForChangersStateSchema.parse({});
      }
    } else {
      if (!student.administrativeOnboardingState) {
        student.administrativeOnboardingState = AdministrativeOnboardingStateSchema.parse({});
      }
      if (student.administrativeOnboardingForChangersState) {
        delete student.administrativeOnboardingForChangersState; // <-- Hide the Tab "FAHRSCHULWECHSEL"
      }
    }
    student.drivingLicenseApplicationStatus =
      student.administrativeOnboardingState?.applicationApproved ??
      student.administrativeOnboardingForChangersState?.changeApproved;
    return student;
  }

  private sumDrivingLessonsCount(types: string[], data?: Record<string, number>): number {
    if (!data || Object.keys(data).length === 0) {
      return 0;
    }

    return types.reduce((acc, type) => acc + (data[type] ?? 0), 0);
  }

  toFirestore(updateData: Partial<Student>, previousData: Student): Partial<StudentDocumentData> {
    const hasPassedTheoryExam = (updateData.theoryKPIs ?? {}).hasPassedTheoryExam;
    const result: Partial<StudentDocumentData> = {
      dateOfBirth: updateData.dateOfBirth?.toString(),
      administrativeOnboardingState: withOnlyDefinedProperties(updateData.administrativeOnboardingState),
      administrativeOnboardingForChangersState: withOnlyDefinedProperties(
        updateData.administrativeOnboardingForChangersState,
      ),
      hubspotContactId: updateData.hubspotContactId,
      examinationAssignmentReceivedAt: updateData.examinationAssignmentReceivedAt,
      examinationAssignmentExpiresAt: updateData.examinationAssignmentExpiresAt,
      ...(updateData.bf17 !== undefined ? { bf17: updateData.bf17 ? true : deleteField() } : {}),
      applicationFeePercentage: updateData.applicationFeePercentage,
      maxBookableDrivingLessonsPerWeekLU: updateData.maxBookableDrivingLessonsPerWeekLU,
      paymentStrategy: updateData.paymentStrategy,
      phoneNumber: updateData.phoneNumber,
      postalAddress: updateData.postalAddress,
      cityOfBirth: updateData.cityOfBirth,
      invoiceAddress: updateData.invoiceAddress,
      ...(Object.keys(updateData.featureToggles ?? {}).length > 0 ? { featureToggles: updateData.featureToggles } : {}),
      budget: updateData.budget,
      minimumSimulatorUE: updateData.minimumSimulatorUE,
      subscriptionPackageDisabled: updateData.subscriptionPackageDisabled,
      subscriptionPackagePausedUntil: updateData.subscriptionPackagePausedUntil?.toISODate(),
      practicalExamLocationUid: updateData.practicalExamLocationUid,
      ...(updateData.dunningDisabled !== undefined
        ? { dunningDisabled: updateData.dunningDisabled ? true : deleteField() }
        : {}),
    };
    if (typeof hasPassedTheoryExam === "boolean") {
      result.theoryKPIs = { hasPassedTheoryExam };
      const bookedTrainingsUpdateData: any = {};
      for (const bookedTraining of previousData.bookedTrainings) {
        if (bookedTraining.drivingSchoolChange) {
          bookedTrainingsUpdateData[bookedTraining.id] = { hasPassedTheoryExam };
        }
      }
      if (Object.keys(bookedTrainingsUpdateData).length) {
        result.bookedTrainings = bookedTrainingsUpdateData;
      }
    }
    return result;
  }

  private async _fetchOne(id: string): Promise<Student> {
    let q = query(collection(firestore, "students"), where("authUserUids", "array-contains", id));
    if (restrictAccessToDrivingSchoolIds?.length === 1) {
      q = query(q, where("currentDrivingSchoolId", "==", restrictAccessToDrivingSchoolIds[0]));
    } else if (restrictAccessToDrivingSchoolIds) {
      q = query(q, where("currentDrivingSchoolId", "in", restrictAccessToDrivingSchoolIds));
    }
    const snapshot = await getDocsFromServer(q);
    if (snapshot.size === 0) {
      throw new Error(`Student ${id} not found`);
    }
    const student = this.fromFirestore(snapshot.docs[0]);
    return student;
  }

  private async _fetchAll(filter?: FilterPayload, drivingSchoolIds?: string[]): Promise<Array<Student>> {
    // We don't need to check restrictAccessToDrivingSchoolIds here,
    // because this is already done in drivingSchoolsProvider ...
    const drivingSchools = drivingSchoolIds
      ? await Promise.all(
          drivingSchoolIds.map(async (drivingSchoolId) => {
            const { data: drivingSchool } = await drivingSchoolsProvider.getOne("drivingSchools", {
              id: drivingSchoolId,
            });
            return drivingSchool;
          }),
        )
      : (await drivingSchoolsProvider.snapshot()).records;
    const students = (
      await Promise.all(
        drivingSchools.map(async (drivingSchool) => {
          const studentsById = await this._fetchStudentsOfDrivingSchool(drivingSchool.id);
          return filter ? applyFilter(studentsById.values(), filter) : [...studentsById.values()];
        }),
      )
    ).flat();
    return students;
  }

  private _fetchStudentsOfDrivingSchool(drivingSchoolId: string): Promise<Map<string, Student>> {
    let promise = this._studentsByIdPromisesByDrivingSchoolId.get(drivingSchoolId);
    if (!promise) {
      promise = this.__fetchStudentsOfDrivingSchool(drivingSchoolId);
      this._studentsByIdPromisesByDrivingSchoolId.set(drivingSchoolId, promise);
    }
    return promise;
  }

  private async __fetchStudentsOfDrivingSchool(drivingSchoolId: string): Promise<Map<string, Student>> {
    if (restrictAccessToDrivingSchoolIds && !restrictAccessToDrivingSchoolIds.includes(drivingSchoolId)) {
      throw new Error(`Access to students of driving school ${drivingSchoolId} denied.`);
    }
    const {
      data: { name: drivingSchool },
    } = await drivingSchoolsProvider.getOne("drivingSchools", { id: drivingSchoolId });
    console.info(`Retrieving all students of ${drivingSchool} ...`);
    const query_ = query(collection(firestore, "students"), where("currentDrivingSchoolId", "==", drivingSchoolId));
    try {
      const t0 = Date.now();
      const firstSnapshot = await getDocsFromServer(query_);
      const studentsByIdFromServer = await this._convertSnapshotToStudentsById(firstSnapshot);
      console.info(`Retrieved ${studentsByIdFromServer.size} student(s) of ${drivingSchool} in ${Date.now() - t0}ms`);
      try {
        this._onNewSnapshot(drivingSchoolId, studentsByIdFromServer);
        return studentsByIdFromServer;
      } finally {
        let log = false; // <-- Don't log the first received snapshot
        let latestSnapshot: undefined | QuerySnapshot;
        let relax = false;
        onSnapshot(query_, async (newSnapshot) => {
          // Ignore incomplete snapshots by checking their length ...
          // From https://github.com/firebase/firebase-js-sdk/issues/5682:
          // "The first snapshot will be raised once any document content is available -
          // this might be before the server responds if there are any document in the cache
          // that match the query. This cache includes all currently active queries even if
          // the cache is only kept in memory (and you have not opted into disk persistence).
          // As the backend starts returning results, documents are then added to the next snapshot.
          // All documents will cause an ADDED change once, but it is correct that this can happen
          // over multiple snapshots."
          if (newSnapshot.docs.length < firstSnapshot.docs.length * 0.95) {
            return;
          }
          latestSnapshot = newSnapshot;
          // We wait a little bit here, to (1) Give the CPU time to do more important stuff
          // and (2) to see if another snapshot arrives in the meantime ...
          await sleep({ milliseconds: relax ? 1500 : 500 });
          const studentsById: Map<string, Student> = new Map();
          let timeout = Date.now() + 100;
          for (const doc of newSnapshot.docs) {
            try {
              if (latestSnapshot !== newSnapshot) {
                // Another snapshot has been received!
                relax = true;
                return;
              }
              const student = studentsProvider.fromFirestore(doc);
              studentsById.set(student.id, student);
            } catch (error) {
              const { path } = doc.ref;
              if (!knownInvalidDocuments.has(path)) {
                knownInvalidDocuments.add(path);
                console.info(`Failed to parse Firestore document ${path} -- ignoring it`, error);
              }
            }
            // Keep the browser responsive if the snapshot is large ...
            if (Date.now() >= timeout) {
              await sleep({ milliseconds: 1 });
              timeout = Date.now() + 100;
            }
          }
          if (log) {
            console.info(`Retrieved new snapshot with ${studentsById.size} student(s) of ${drivingSchool}`);
          } else {
            log = true;
          }
          this._onNewSnapshot(drivingSchoolId, studentsById);
          relax = false;
        });
      }
    } catch (error) {
      reportError(`Failed to retrieve all students of ${drivingSchool}`, { error, query: query_ });
      return new Map();
    }
  }

  async fetchProviderData(studentUid: string) {
    try {
      const user = await serverAPI.getUser(studentUid);
      return user.providerData;
    } catch (error) {
      reportError(`Failed to fetch auth user of student with ID ${studentUid}`, error);
    }
  }

  private async _convertSnapshotToStudentsById(snapshot: QuerySnapshot): Promise<Map<string, Student>> {
    const studentsById: Map<string, Student> = new Map();
    let timeout = Date.now() + 100;
    for (const doc of snapshot.docs) {
      try {
        const student = studentsProvider.fromFirestore(doc);
        studentsById.set(student.id, student);
        this.studentsByAutovioUserId.set(student.autovioUserId, student);
      } catch (error) {
        const { path } = doc.ref;
        if (!knownInvalidDocuments.has(path)) {
          knownInvalidDocuments.add(path);
          console.info(`Failed to parse Firestore document ${path} -- ignoring it`, error);
        }
      }
      // Keep the browser responsive if the snapshot is large ...
      if (Date.now() >= timeout) {
        await sleep({ milliseconds: 1 });
        timeout = Date.now() + 100;
      }
    }
    return studentsById;
  }

  private _onNewSnapshot(drivingSchoolId: string, studentsById: Map<string, Student>): void {
    this._studentsByIdByDrivingSchoolId.set(drivingSchoolId, studentsById);
    this._studentsByIdPromisesByDrivingSchoolId.set(drivingSchoolId, Promise.resolve(studentsById));
  }

  private _convertSort(sort: SortPayload): SortPayload {
    if (!this._webinarMode) {
      return sort;
    }
    if (sort.field === "name") {
      return { ...sort, field: "_name" };
    } else if (sort.field === "firstName") {
      return { ...sort, field: "_firstName" };
    } else if (sort.field === "lastName") {
      return { ...sort, field: "_lastName" };
    } else {
      return sort;
    }
  }

  private _convertStudent(
    student: Omit<
      Student,
      "firstName" | "callName" | "lastName" | "name" | "phoneNumber" | "postalAddress" | "invoiceAddress"
    > & {
      _firstName: Student["firstName"];
      _callName: Student["callName"];
      _lastName: Student["lastName"];
      _name: Student["name"];
      _phoneNumber: Student["phoneNumber"];
      _postalAddress: Student["postalAddress"];
      _invoiceAddress: Student["invoiceAddress"];
    },
  ): Student {
    const { _firstName, _callName, _lastName, _name, _phoneNumber, _postalAddress, _invoiceAddress } = student;
    if (this._webinarMode) {
      (student as any).firstName = _xxx(_firstName);
      (student as any).callName = _xxx(_callName);
      (student as any).lastName = _xxx(_lastName);
      (student as any).name = _xxx(_name);
      (student as any).phoneNumber = _xxx(_phoneNumber);
      (student as any).postalAddress = {
        street: _xxx(_postalAddress.street) ?? "",
        postalCode: _xxx(_postalAddress.postalCode) ?? "",
        city: _xxx(_postalAddress.city) ?? "",
        geoCoordinates: _postalAddress.geoCoordinates,
      } satisfies PostalAddress;
      (student as any).invoiceAddress = _invoiceAddress
        ? ({
            type: "InvoiceAddress",
            firstName: _xxx(_invoiceAddress.firstName),
            lastName: _xxx(_invoiceAddress.lastName),
            company: _xxx(_invoiceAddress.company),
            street: _xxx(_invoiceAddress.street),
            postalCode: _xxx(_invoiceAddress.postalCode),
            city: _xxx(_invoiceAddress.city),
            email: _xxx(_invoiceAddress.email),
          } satisfies InvoiceAddress)
        : _invoiceAddress;
    } else {
      (student as any).firstName = _firstName;
      (student as any).callName = _callName;
      (student as any).lastName = _lastName;
      (student as any).name = _name;
      (student as any).phoneNumber = _phoneNumber;
      (student as any).postalAddress = _postalAddress;
      (student as any).invoiceAddress = _invoiceAddress;
    }
    return student as any as Student;
  }

  private async _fetchAusbildungsnachweisSignatureRequests(
    studentIds: Array<string>,
  ): Promise<Array<{ studentUid: string; state: string }>> {
    const studentIdsChunks = _chunk(
      studentIds,
      30, // Max 30 student ids per query because of Firestore's "in" operator limit
    );
    const snapshotChunks = await Promise.all(
      studentIdsChunks.map((studentIdsChunk) =>
        getDocsFromServer(
          query(
            collection(firestore, "signature_requests"),
            where("studentUid", "in", studentIdsChunk),
            where("type", "==", "ausbildungsnachweis"),
          ),
        ),
      ),
    );
    return snapshotChunks.flatMap((snap) =>
      snap.docs.map((doc) => doc.data() as { studentUid: string; state: string }),
    );
  }
}

export const studentsProvider = new StudentsProvider();

function _renderVerboseStatusSinceXxxDays(student: Student): string {
  const { lastStatusChangeAt, verboseStatus } = student;
  if (!lastStatusChangeAt) {
    return verboseStatus;
  }
  let d1 = DateTime.now();
  let d2 = lastStatusChangeAt;
  d1 = DateTime.fromObject({ year: d1.year, month: d1.month, day: d1.day }, { zone: d1.zone });
  d2 = DateTime.fromObject({ year: d2.year, month: d2.month, day: d2.day }, { zone: d2.zone });
  const dateDiff = Math.round(d1.diff(d2, "days").days);
  const since = dateDiff === 0 ? "heute" : dateDiff === 1 ? "gestern" : `${dateDiff} Tagen`;
  if (verboseStatus.endsWith(")")) {
    return `${verboseStatus.substring(0, verboseStatus.length - 1)}, seit ${since})`;
  } else {
    return `${verboseStatus} (seit ${since})`;
  }
}

function _inferActiveOrMostRecentBookedTraining(student: Student): BookedTraining {
  const { bookedTrainings } = student;
  // Fast path if student has only one booked training ...
  if (bookedTrainings.length === 1) {
    return bookedTrainings[0];
  }
  // If all booked trainings are finished, we return the most recent one ...
  if (bookedTrainings.every((it) => it.isFinished)) {
    return bookedTrainings.reduce((bookedTraining1: BookedTraining, bookedTraining2: BookedTraining) => {
      const d1 = bookedTraining1.kpis?.mostRecentDrivingLessonDate ?? DateTime.fromMillis(0);
      const d2 = bookedTraining2.kpis?.mostRecentDrivingLessonDate ?? DateTime.fromMillis(0);
      return d1 > d2 ? bookedTraining1 : bookedTraining2;
    });
  }
  // Otherwise we return the active booked training ...
  return _inferActiveBookedTraining(student);
}

function _inferActiveBookedTraining(student: Student): BookedTraining {
  const { bookedTrainings } = student;
  if (!bookedTrainings.length) {
    throw new Error(`Student ${student.id} (${student.name}) has no booked trainings.`);
  }
  const candidates = bookedTrainings.filter((it) => !it.isFinished);
  if (!candidates.length) {
    throw new Error(`All booked trainings of student ${student.id} (${student.name}) are completed.`);
  }
  // Fast path if there is only one candidate ...
  if (candidates.length === 1) {
    return candidates[0];
  }
  // If there are multiple candidates, we prefer the one with the most recent driving lesson ...
  const candidatesWithDrivingLessons = candidates.filter((it) => it.kpis?.mostRecentDrivingLessonDate);
  if (candidatesWithDrivingLessons.length === 1) {
    return candidates[0];
  } else if (candidatesWithDrivingLessons.length > 1) {
    return candidatesWithDrivingLessons.reduce((a, b) =>
      a.kpis!.mostRecentDrivingLessonDate! > b.kpis!.mostRecentDrivingLessonDate! ? a : b,
    );
  }
  // If the student has no driving lesson yet, we use the following order ...
  for (const drivingLicenseClasses of [
    CAR_ONLY_DRIVING_LICENSE_CLASSES,
    MOTORCYCLE_DRIVING_LICENSE_CLASSES,
    CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
    TRACTOR_DRIVING_LICENSE_CLASSES,
    TRUCK_ONLY_DRIVING_LICENSE_CLASSES,
    TRUCK_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
    BUS_ONLY_DRIVING_LICENSE_CLASSES,
    BUS_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
  ]) {
    const bestCandidate = candidates.find((it) => drivingLicenseClasses.has(it.drivingLicenseClass));
    if (bestCandidate) {
      return bestCandidate;
    }
  }
  console.warn(
    `Failed to infer active booked training of student ${student.id} (${student.name}) -- falling back to student.bookedTrainings[0]`,
  );
  return student.bookedTrainings[0];
}

function _inferProgressMilestones(
  student: Student,
  schaltkompetenzDrivingLessonsCount: number | undefined,
): ProgressMilestone[] {
  const milestones: ProgressMilestone[] = [];
  if (student.drivingLicenseApplicationStatus === "approved") {
    milestones.push("fullyRegistered");
  }
  if (student.hasPassedTheoryExam) {
    milestones.push("theoryExamPassed");
  }
  if (student.kpis.finishedNormalDrivingLessonsCount >= 10) {
    milestones.push("minNormalDrivingLessonsDone");
  }
  if (schaltkompetenzDrivingLessonsCount && schaltkompetenzDrivingLessonsCount >= 1) {
    milestones.push("schaltkompetenzDone");
  }
  const compulsorySpecialDrivingLessonsCount = Object.values(
    student.activeOrMostRecentBookedTraining.compulsorySpecialDrivingLessons ?? {},
  ).reduce((acc, it) => acc + it, 0);
  if (student.kpis.finishedSpecialDrivingLessonsCount > compulsorySpecialDrivingLessonsCount) {
    milestones.push("specialDrivingLessonsDone");
  }
  return milestones;
}

function _xxx(s: string): string;
function _xxx(s: string | undefined): string | undefined;
function _xxx(s: string | undefined) {
  if (typeof s !== "string") {
    return s;
  }
  const a = [];
  for (let i = 0; i < s.length; i++) {
    a.push(s[i].toLowerCase() !== s[i] ? "X" : s[i]);
  }
  return a
    .join("")
    .replaceAll(/[0-9]/g, "X")
    .replaceAll(/[^X +-]/gi, "x");
}

function _callName(data: StudentDocumentData): string {
  const firstName = data.publicProfile?.firstName?.trim() || "";
  if (!firstName) {
    return "???";
  }
  const i = firstName.indexOf(" ");
  return capitalize(i > 0 ? firstName.substring(0, i) : firstName);
}

function _getEffectiveFeatureToggles(
  data: undefined | Partial<FeatureTogglesForStudents>,
  bookedTrainings: Array<BookedTraining>,
  billingProvider: BillingProvider | undefined,
): FeatureTogglesForStudents {
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  // Please keep this function in sync with mobile-app/services/lib/src/utils/firestore_utils.dart //
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  data ??= {};
  if (bookedTrainings.length === 0) {
    if (typeof data.canLearnTheoryInMobileApp !== "boolean") {
      data.canLearnTheoryInMobileApp = true;
    }
    if (typeof data.canBookTheoryLessons !== "boolean") {
      data.canBookTheoryLessons = false;
    }
    if (typeof data.canBookDrivingLessons !== "boolean") {
      data.canBookDrivingLessons = false;
    }
  } else {
    if (typeof data.canLearnTheoryInMobileApp !== "boolean") {
      data.canLearnTheoryInMobileApp = bookedTrainings.some((it) => it.includesTheoryExam && !it.drivingSchoolChange);
    }
    if (typeof data.canBookTheoryLessons !== "boolean") {
      data.canBookTheoryLessons = bookedTrainings.some(
        (it) => it.hasCompulsoryTheoryLessons && !it.drivingSchoolChange,
      );
    }
    if (typeof data.canBookDrivingLessons !== "boolean") {
      data["canBookDrivingLessons"] = !bookedTrainings.every((it) => it.isASF);
    }
  }
  if (billingProvider === "datapart") {
    data.canHavePrepaidCredits = false;
    data.canHaveFreeCredits = false;
    data.canCreateManualInvoices = false;
    data.canCreatePayment = false;
    data.canViewInvoiceDetails = false;
  }
  return FeatureTogglesForStudentsSchema.parse(data);
}

const _BookedQuoteSchema = z.object({
  bundleName: z.string(),
  startDate: DateWithoutTimeSchema.optional(),
  endDate: DateWithoutTimeSchema.optional(),
});

function bookedQuotesFromFirestore(bookedQuotesData: { [quoteId: string]: any }): Array<BookedQuote> {
  return Object.entries(bookedQuotesData).map(([quoteId, data]) => {
    return {
      quoteId,
      ..._BookedQuoteSchema.parse(data),
    };
  });
}
